import { ArrowDownward, ArrowUpward, DragIndicator } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const ColumnSelectionTab = ({ availableColumns = [], filters, updateFilter }) => {
  const columns = useMemo(() => {
    if (!Array.isArray(availableColumns)) {
      return [];
    }
    return availableColumns;
  }, [availableColumns]);

  const columnFilterValue = filters?.get("columns") || "";

  const selectedColumnsFromFilters = useMemo(() => {
    if (!columnFilterValue) {
      return [];
    }

    return columnFilterValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }, [columnFilterValue]);

  const [checkedAvailable, setCheckedAvailable] = useState([]);
  const [checkedSelected, setCheckedSelected] = useState([]);
  const [availableSearch, setAvailableSearch] = useState("");
  const [draggedFieldName, setDraggedFieldName] = useState(null);
  const [dragOverFieldName, setDragOverFieldName] = useState(null);

  const validFieldNames = useMemo(
    () =>
      new Set(
        columns
          .map((column) => column?.field_name)
          .filter(Boolean)
      ),
    [columns]
  );
  const selectedColumnsSet = useMemo(
    () => new Set(selectedColumnsFromFilters),
    [selectedColumnsFromFilters]
  );
  const selectedFieldsByOrder = useMemo(() => {
    const seen = new Set();
    return selectedColumnsFromFilters.filter((fieldName) => {
      if (!validFieldNames.has(fieldName) || seen.has(fieldName)) {
        return false;
      }
      seen.add(fieldName);
      return true;
    });
  }, [selectedColumnsFromFilters, validFieldNames]);
  const availableFieldsByOrder = useMemo(
    () =>
      columns
        .map((column) => column?.field_name)
        .filter((fieldName) => fieldName && !selectedColumnsSet.has(fieldName)),
    [columns, selectedColumnsSet]
  );
  const columnLookup = useMemo(
    () =>
      columns.reduce((accumulator, column, index) => {
        const fieldName = column?.field_name;
        if (!fieldName) {
          return accumulator;
        }
        accumulator[fieldName] = {
          display: column?.display || `Column ${index + 1}`,
        };
        return accumulator;
      }, {}),
    [columns]
  );
  const filteredAvailableFields = useMemo(() => {
    const query = availableSearch.trim().toLowerCase();
    if (!query) {
      return availableFieldsByOrder;
    }

    return availableFieldsByOrder.filter((fieldName) => {
      const displayText = (columnLookup[fieldName]?.display || "").toLowerCase();
      return displayText.includes(query) || fieldName.toLowerCase().includes(query);
    });
  }, [availableFieldsByOrder, availableSearch, columnLookup]);
  const totalColumnsCount = columns.length;
  const availableColumnsCount = availableFieldsByOrder.length;
  const selectedColumnsCount = selectedFieldsByOrder.length;

  useEffect(() => {
    setCheckedAvailable((previous) =>
      previous.filter((fieldName) => availableFieldsByOrder.includes(fieldName))
    );
    setCheckedSelected((previous) =>
      previous.filter((fieldName) => selectedFieldsByOrder.includes(fieldName))
    );
  }, [availableFieldsByOrder, selectedFieldsByOrder]);

  useEffect(() => {
    if (draggedFieldName && !selectedFieldsByOrder.includes(draggedFieldName)) {
      setDraggedFieldName(null);
    }
    if (dragOverFieldName && !selectedFieldsByOrder.includes(dragOverFieldName)) {
      setDragOverFieldName(null);
    }
  }, [dragOverFieldName, draggedFieldName, selectedFieldsByOrder]);

  const toggleChecked = (fieldName, setCheckedItems) => {
    setCheckedItems((previous) =>
      previous.includes(fieldName)
        ? previous.filter((value) => value !== fieldName)
        : [...previous, fieldName]
    );
  };

  const moveToSelected = () => {
    if (!checkedAvailable.length) {
      return;
    }

    const toMoveSet = new Set(checkedAvailable);
    const fieldsToAppend = availableFieldsByOrder.filter((fieldName) => toMoveSet.has(fieldName));
    const nextSelected = [...selectedFieldsByOrder, ...fieldsToAppend];
    updateFilter("columns", nextSelected.length ? nextSelected.join(",") : null);
    setCheckedAvailable([]);
  };

  const moveToAvailable = () => {
    if (!checkedSelected.length) {
      return;
    }

    const toRemoveSet = new Set(checkedSelected);
    const nextSelected = selectedFieldsByOrder.filter((fieldName) => !toRemoveSet.has(fieldName));
    updateFilter("columns", nextSelected.length ? nextSelected.join(",") : null);
    setCheckedSelected([]);
  };

  const reorderSelectedColumns = (sourceFieldName, targetFieldName) => {
    if (!sourceFieldName || !targetFieldName || sourceFieldName === targetFieldName) {
      return;
    }

    const sourceIndex = selectedFieldsByOrder.indexOf(sourceFieldName);
    const targetIndex = selectedFieldsByOrder.indexOf(targetFieldName);
    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const reordered = [...selectedFieldsByOrder];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    updateFilter("columns", reordered.length ? reordered.join(",") : null);
  };

  const renderColumnList = (
    fieldNames,
    checkedItems,
    setCheckedItems,
    emptyMessage,
    allowReorder = false,
    listClassName = "h-[220px]"
  ) => (
    <List dense className={`${listClassName} overflow-y-auto p-0`}>
      {fieldNames.length ? (
        fieldNames.map((fieldName) => (
          <ListItemButton
            key={fieldName}
            role="listitem"
            className={
              allowReorder && dragOverFieldName === fieldName
                ? "bg-sky-50 border-y border-sky-200"
                : ""
            }
            onClick={() => toggleChecked(fieldName, setCheckedItems)}
            onDragOver={(event) => {
              if (!allowReorder) {
                return;
              }
              event.preventDefault();
              setDragOverFieldName(fieldName);
            }}
            onDrop={(event) => {
              if (!allowReorder) {
                return;
              }
              event.preventDefault();
              reorderSelectedColumns(draggedFieldName, fieldName);
              setDraggedFieldName(null);
              setDragOverFieldName(null);
            }}
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={checkedItems.includes(fieldName)}
                disableRipple
                tabIndex={-1}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <div className="flex items-center justify-between gap-2">
                  <Typography className="text-sm text-black">
                    {columnLookup[fieldName]?.display || fieldName}
                  </Typography>
                  {allowReorder && (
                    <div
                      draggable
                      className="inline-flex cursor-grab text-gray-500 active:cursor-grabbing"
                      onClick={(event) => event.stopPropagation()}
                      onDragStart={(event) => {
                        event.stopPropagation();
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", fieldName);
                        setDraggedFieldName(fieldName);
                      }}
                      onDragEnd={() => {
                        setDraggedFieldName(null);
                        setDragOverFieldName(null);
                      }}
                      title="Drag to reorder"
                    >
                      <DragIndicator fontSize="small" />
                    </div>
                  )}
                </div>
              }
            />
          </ListItemButton>
        ))
      ) : (
        <Typography className="p-4 text-sm text-gray-500">{emptyMessage}</Typography>
      )}
    </List>
  );

  if (!columns.length) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <Typography className="text-sm text-gray-500">No columns available.</Typography>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col p-4 pb-0">
      <Paper
        variant="outlined"
        className="flex h-[30%] min-h-0 flex-col"
        sx={{ borderColor: "rgba(0, 0, 0, 0.3)" }}
      >
        <Typography className="px-4 pt-2 pb-1 text-sm font-semibold text-gray-700">
          Available Columns ({availableColumnsCount} / {totalColumnsCount})
        </Typography>
        <div className="px-3 pb-2">
          <TextField
            size="small"
            fullWidth
            placeholder="Search available columns"
            value={availableSearch}
            onChange={(event) => setAvailableSearch(event.target.value)}
          />
        </div>
        <Divider />
        {renderColumnList(
          filteredAvailableFields,
          checkedAvailable,
          setCheckedAvailable,
          availableSearch.trim() ? "No matching columns." : "No available columns.",
          false,
          "min-h-0 flex-1"
        )}
      </Paper>

      <div className="my-2 flex shrink-0 justify-center gap-2">
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowDownward />}
          onClick={moveToSelected}
          disabled={!checkedAvailable.length}
        >
          Add
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowUpward />}
          onClick={moveToAvailable}
          disabled={!checkedSelected.length}
        >
          Remove
        </Button>
      </div>

      <Paper
        variant="outlined"
        className="flex min-h-0 flex-1 flex-col"
        sx={{ borderColor: "rgba(0, 0, 0, 0.3)" }}
      >
        <Typography className="px-4 py-2 text-sm font-semibold text-gray-700">
          Selected Columns ({selectedColumnsCount} / {totalColumnsCount})
        </Typography>
        <Divider />
        {renderColumnList(
          selectedFieldsByOrder,
          checkedSelected,
          setCheckedSelected,
          "No selected columns.",
          true,
          "min-h-0 flex-1"
        )}
      </Paper>
    </div>
  );
};

export default ColumnSelectionTab;
